import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, API, errMsg } from '../../lib/api';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Megaphone, Copy, QrCode } from '@phosphor-icons/react';

export const CampaignsPanel = () => {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: '', source: '', medium: '' });
  const { data: camps } = useQuery({ queryKey: ['adm-campaigns'], queryFn: () => api.get('/admissions/campaigns').then((r) => r.data) });
  const inval = () => qc.invalidateQueries({ queryKey: ['adm-campaigns'] });
  const createMut = useMutation({
    mutationFn: () => api.post('/admissions/campaigns', form),
    onSuccess: () => { toast.success('Campaign created - copy its link into your ads'); setForm({ name: '', source: '', medium: '' }); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, active }) => api.patch(`/admissions/campaigns/${id}`, { active }),
    onSuccess: inval, onError: (e) => toast.error(errMsg(e)),
  });
  const copyLink = (c) => {
    navigator.clipboard.writeText(`${window.location.origin}${c.link_path}`);
    toast.success('Campaign link copied');
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 mb-5" data-testid="campaigns-panel">
      <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2 flex items-center gap-1.5"><Megaphone size={14} className="text-orange-600" />Campaigns</p>
      <div className="flex flex-wrap gap-2 mb-3">
        <Input data-testid="campaign-name-input" placeholder="Campaign name (e.g. Summer Open Day)" className="h-8 text-xs flex-1 min-w-[160px]" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Input data-testid="campaign-source-input" placeholder="Source (facebook, google...)" className="h-8 text-xs w-40" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
        <Input data-testid="campaign-medium-input" placeholder="Medium (cpc, email...)" className="h-8 text-xs w-36" value={form.medium} onChange={(e) => setForm({ ...form, medium: e.target.value })} />
        <Button data-testid="campaign-create" size="sm" variant="outline" disabled={!form.name || createMut.isPending} onClick={() => createMut.mutate()}>Create</Button>
      </div>
      {(camps || []).length > 0 && (
        <table className="w-full text-sm" data-testid="campaigns-table">
          <thead><tr className="text-left">
            {['Campaign', 'Link', 'Leads', 'Enrolled', 'Conversion', 'Status'].map((h) => <th key={h} className="py-1.5 pe-2 font-mono-label text-[9px] uppercase text-slate-400">{h}</th>)}
          </tr></thead>
          <tbody>
            {camps.map((c) => (
              <tr key={c.id} className="border-t border-slate-50" data-testid={`campaign-row-${c.id}`}>
                <td className="py-2 pe-2">
                  <span className="font-medium">{c.name}</span>
                  {(c.source || c.medium) && <span className="block text-[10px] text-slate-400">{[c.source, c.medium].filter(Boolean).join(' · ')}</span>}
                </td>
                <td className="py-2 pe-2">
                  {c.link_path
                    ? <span className="flex gap-2 items-center">
                        <button data-testid={`campaign-copy-${c.id}`} onClick={() => copyLink(c)} className="text-xs text-orange-700 underline flex items-center gap-1"><Copy size={12} />copy link</button>
                        <a data-testid={`campaign-poster-${c.id}`} href={`${API}/admissions/campaigns/${c.id}/poster.pdf`} title="Download QR poster"
                          className="text-xs text-slate-600 underline flex items-center gap-1 hover:text-orange-700"><QrCode size={13} />poster</a>
                      </span>
                    : <span className="text-xs text-slate-400">-</span>}
                </td>
                <td className="py-2 pe-2 font-semibold" data-testid={`campaign-leads-${c.id}`}>{c.leads}</td>
                <td className="py-2 pe-2">{c.enrolled}</td>
                <td className="py-2 pe-2">{c.conversion_pct}%</td>
                <td className="py-2">
                  <button data-testid={`campaign-toggle-${c.id}`} onClick={() => toggleMut.mutate({ id: c.id, active: !c.active })}
                    className={`font-mono-label text-[9px] uppercase px-2 py-0.5 rounded ${c.active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`}>
                    {c.active ? 'active' : 'archived'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(camps || []).length === 0 && <p className="text-sm text-slate-400">No campaigns yet - create one and use its link in ads, posts or QR codes to track which channel wins.</p>}
    </div>
  );
};
